import React, { useEffect, useMemo, useRef, useState } from "react";
import { SiteHeader } from "../components/layout/SiteHeader";
import { ThemeProvider } from "../components/providers/ThemeProvider";
import { RootErrorBoundary } from "../components/error-boundary/RootErrorBoundary";
import { Footer } from "../components/layout/Footer";
import { Head, usePage } from "@inertiajs/react";
import ProfileCompletionModal from "../components/ProfileCompletionModal";
import { useToast } from "../components/ui/toast";
import logoUrl from "@/assets/logo.svg";
import posthog from "posthog-js";
import { posthogState } from "../lib/posthogState";

const BASE_URL = "https://infralyst.io";

export default function Layout({
  children,
  title,
  description,
  showGradient = true,
  image = "og.png",
}: {
  children: React.ReactNode;
  title?: string;
  description?: string;
  showGradient?: boolean;
  image?: string;
}) {
  const { user, flash } = usePage<{
    user?: {
      id: number;
      uuid: string;
      email: string;
      name?: string;
      needs_profile: boolean;
    };
    flash?: {
      notice?: string;
      alert?: string;
    };
  }>().props;
  const { showToast } = useToast();
  const defaultTitle = "Automated Terraform PRs to cut AWS costs";
  const defaultDescription =
    "Infralyst finds underused AWS resources, analyzes real utilization data, and turns savings into ready to merge Terraform pull requests.";

  const finalTitle = `Infralyst | ${title || defaultTitle}`;
  const finalDescription = description || defaultDescription;

  // Use client-side origin for images to avoid hydration mismatch
  const [baseUrl, setBaseUrl] = useState(BASE_URL);
  const finalImage = `${baseUrl}/${image.replace(/^\//, "")}`; // Remove leading slash
  const finalLogo = `${baseUrl}${logoUrl}`;

  useEffect(() => {
    setBaseUrl(window.location.origin);
  }, []);

  const prevUuid = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!posthogState.loaded) return;
    const uuid = user?.uuid;
    if (uuid) {
      posthog.identify(uuid);
    } else if (prevUuid.current) {
      posthog.reset();
    }
    prevUuid.current = uuid;
  }, [user?.uuid]);

  // Auto-convert flash messages to toasts
  useEffect(() => {
    if (flash?.notice) {
      showToast("success", flash.notice);
    }
    if (flash?.alert) {
      showToast("error", flash.alert);
    }
  }, [flash, showToast]);

  const organizationJsonLd = useMemo(
    () =>
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Infralyst",
        url: "https://infralyst.io",
        logo: finalLogo,
        description: finalDescription,
        sameAs: [
          "https://www.linkedin.com/company/infralyst/",
          "https://x.com/infralyst",
          "https://github.com/infralyst/",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          email: "support@infralyst.io",
          contactType: "customer support",
        },
      }),
    [finalLogo, finalDescription]
  );

  return (
    <>
      <Head>
        <title>{finalTitle}</title>
        <meta name="description" content={finalDescription} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={finalTitle} />
        <meta property="og:description" content={finalDescription} />
        <meta property="og:image" content={finalImage} />
        <meta property="og:logo" content={finalLogo} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={finalTitle} />
        <meta name="twitter:description" content={finalDescription} />
        <meta name="twitter:image" content={finalImage} />

        {/* Organization structured data */}
        <script type="application/ld+json">{organizationJsonLd}</script>
      </Head>
      {showGradient && (
        <div className="fixed inset-0 -z-10 bg-background">
          <div className="absolute top-0 z-[-2] h-screen w-screen bg-background overflow-hidden">
            <div className="absolute left-[15%] top-[40%] h-[600px] w-[600px] rounded-full bg-gradient-to-r from-purple-500/15 to-violet-400/15 blur-[120px] max-w-[90vw]" />
            <div className="absolute right-[15%] top-[50%] h-[600px] w-[600px] rounded-full bg-gradient-to-l from-purple-600/15 to-violet-500/15 blur-[120px] max-w-[90vw]" />
          </div>
        </div>
      )}

      <ThemeProvider>
        <RootErrorBoundary>
          <div className="flex flex-col min-h-screen relative">
            <SiteHeader />
            <main className="flex-1 px-4">{children}</main>
            <Footer />
          </div>
          {user && <ProfileCompletionModal isOpen={user.needs_profile} />}
        </RootErrorBoundary>
      </ThemeProvider>
    </>
  );
}
